// app/src/simulations/runContributeV2Verify.ts
import { Buffer } from "buffer";
import { Connection, PublicKey } from "@solana/web3.js";
import { decodeAccountByIdl } from "../solana/idlDecode";

type Commitment = "processed" | "confirmed" | "finalized";

type RuntimeConfig = {
  APP_ENV?: string;
  endpoint?: string;
  programId?: string;
  PHASE?: string;
  LOG_LEVEL?: string;
};

function asTrimmed(v: unknown) {
  return String(v ?? "").trim();
}

/**
 * Reads runtime config WITHOUT importing /src/config.ts.
 * Priority:
 *  1) window.__WEARE_SUSU_RUNTIME_CONFIG__ (best for your hosted + dev scenarios)
 *  2) import.meta.env.VITE_* (standard Vite)
 *  3) hard defaults (testnet)
 */
function resolveConfig(): { endpoint: string; programId: string; source: string } {
  const w = typeof window !== "undefined" ? (window as any) : {};
  const rt: RuntimeConfig =
    (w.__WEARE_SUSU_RUNTIME_CONFIG__ ||
      w.__WEARE_SUSU_RUNTIME__ ||
      w.__WEARE_SUSU_CONFIG__ ||
      {}) as RuntimeConfig;

  const vite = (import.meta as any)?.env || {};

  const programId =
    asTrimmed(rt.programId) ||
    asTrimmed(vite.VITE_PROGRAM_ID) ||
    asTrimmed(vite.VITE_WEARE_SUSU_PROGRAM_ID) ||
    "";

  const endpoint =
    asTrimmed(rt.endpoint) ||
    asTrimmed(vite.VITE_SOLANA_RPC) ||
    asTrimmed(vite.VITE_SOLANA_ENDPOINT) ||
    "https://api.testnet.solana.com";

  const source =
    asTrimmed(rt.programId) || asTrimmed(rt.endpoint) ? "runtime" : asTrimmed(vite.VITE_PROGRAM_ID) ? "vite" : "default";

  if (!programId) {
    throw new Error(
      "ProgramId is not configured.\n" +
        "Fix:\n" +
        "- Preferred: set VITE_PROGRAM_ID in app/.env.<mode>\n" +
        "- Or: set window.__WEARE_SUSU_RUNTIME_CONFIG__.programId before app loads\n" +
        `Debug:\n- runtime.programId=${String(rt.programId)}\n- vite.VITE_PROGRAM_ID=${String(vite.VITE_PROGRAM_ID)}`
    );
  }

  // Validate format early
  try {
    // eslint-disable-next-line no-new
    new PublicKey(programId);
  } catch {
    throw new Error(`Invalid programId (not a PublicKey): ${programId}`);
  }

  return { endpoint, programId, source };
}

function deriveMemberV2Pda(programId: PublicKey, poolV2: PublicKey, wallet: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("member_v2"), poolV2.toBuffer(), wallet.toBuffer()],
    programId
  )[0];
}

function deriveVaultV2Pda(programId: PublicKey, poolV2: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from("vault_v2"), poolV2.toBuffer()], programId)[0];
}

export async function runContributeV2Verify(input: {
  pool_v2: string;

  wallet?: string; // optional override; default uses Phantom if available
  member_v2?: string; // optional override; otherwise derived
  vault_v2?: string; // optional override; otherwise derived

  commitment?: Commitment;
}) {
  const { endpoint, programId: programIdStr, source } = resolveConfig();
  const commitment: Commitment = input.commitment ?? "confirmed";

  const connection = new Connection(endpoint, commitment);

  const programId = new PublicKey(programIdStr);
  const poolPk = new PublicKey(asTrimmed(input.pool_v2));

  // wallet pubkey: Phantom default or provided
  const w = typeof window !== "undefined" ? (window as any) : {};
  const phantomPk = w?.solana?.publicKey?.toString?.() ?? "";
  const walletStr = asTrimmed(input.wallet) || phantomPk;

  if (!walletStr) {
    throw new Error("Wallet pubkey not available. Connect Phantom or pass wallet.");
  }

  const walletPk = new PublicKey(walletStr);

  // Derived PDAs
  const derivedMember = deriveMemberV2Pda(programId, poolPk, walletPk);
  const derivedVault = deriveVaultV2Pda(programId, poolPk);

  // Use overrides if supplied; otherwise derived
  const memberOverride = asTrimmed(input.member_v2);
  const vaultOverride = asTrimmed(input.vault_v2);

  const memberPk = memberOverride ? new PublicKey(memberOverride) : derivedMember;
  const vaultPk = vaultOverride ? new PublicKey(vaultOverride) : derivedVault;

  // Strict override validation (best practice: fail fast if mismatch)
  const overrideIssues: string[] = [];
  if (memberOverride && !memberPk.equals(derivedMember)) {
    overrideIssues.push(
      `member_v2 mismatch:\n- provided=${memberPk.toBase58()}\n- derived=${derivedMember.toBase58()}`
    );
  }
  if (vaultOverride && !vaultPk.equals(derivedVault)) {
    overrideIssues.push(
      `vault_v2 mismatch:\n- provided=${vaultPk.toBase58()}\n- derived=${derivedVault.toBase58()}`
    );
  }
  if (overrideIssues.length) {
    throw new Error(`Override validation failed:\n\n${overrideIssues.join("\n\n")}`);
  }

  // Fetch + decode PoolV2
  const poolInfo = await connection.getAccountInfo(poolPk, commitment);
  if (!poolInfo) throw new Error(`PoolV2 account not found: ${poolPk.toBase58()}`);

  const poolDecoded = await decodeAccountByIdl(poolInfo.data);
  if (!poolDecoded.ok) throw new Error(`Pool decode failed: ${poolDecoded.reason}`);

  // Fetch + decode MemberV2
  const memberInfo = await connection.getAccountInfo(memberPk, commitment);
  if (!memberInfo) throw new Error(`MemberV2 account not found: ${memberPk.toBase58()}`);

  const memberDecoded = await decodeAccountByIdl(memberInfo.data);
  if (!memberDecoded.ok) throw new Error(`Member decode failed: ${memberDecoded.reason}`);

  // Vault balance
  const vaultBal = await connection.getBalance(vaultPk, commitment);
  const vaultRentBaseline = await connection.getMinimumBalanceForRentExemption(0);

  return {
    context: {
      endpoint,
      programId: programId.toBase58(),
      commitment,
      wallet: walletPk.toBase58(),
      configSource: source,
    },
    addresses: {
      pool_v2: poolPk.toBase58(),
      member_v2: memberPk.toBase58(),
      vault_v2: vaultPk.toBase58(),
      derived: {
        member_v2: derivedMember.toBase58(),
        vault_v2: derivedVault.toBase58(),
      },
    },
    decoded: {
      pool: { accountName: poolDecoded.accountName, data: poolDecoded.decoded },
      member: { accountName: memberDecoded.accountName, data: memberDecoded.decoded },
    },
    vault: {
      lamports: vaultBal,
      rentExemptBaselineLamports: vaultRentBaseline,
      spendableLamportsEstimate: Math.max(0, vaultBal - vaultRentBaseline),
    },
  };
}
